import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, RefreshCw, Trash2, Check, AlertTriangle, Download, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RowData {
  id?: string;
  account_number: string;
  [key: string]: any;
}

interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

interface ChangeDetectionResult {
  added: RowData[];
  updated: Array<{
    accountNumber: string;
    changes: FieldChange[];
    oldRow: RowData;
    newRow: RowData;
  }>;
  deleted: RowData[];
  unchanged: RowData[];
}

interface ReuploadChangeDetectionProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: RowData[];
  newFileName: string;
  newData: RowData[];
  onConfirm: (changes: ChangeDetectionResult) => Promise<void>;
}

/**
 * ReuploadChangeDetection - Change detection preview modal
 * 
 * Requirement 6: Re-upload with change detection showing what will be
 * added, updated, deleted before applying changes.
 */
export default function ReuploadChangeDetection({
  isOpen,
  onClose,
  currentData,
  newFileName,
  newData,
  onConfirm,
}: ReuploadChangeDetectionProps) {
  const [changes, setChanges] = useState<ChangeDetectionResult | null>(null);
  const [activeTab, setActiveTab] = useState<'updated' | 'added' | 'deleted'>('updated');
  const [isLoading, setIsLoading] = useState(false);

  // Detect changes when data is provided
  useEffect(() => {
    if (isOpen && currentData.length > 0 && newData.length > 0) {
      const detected = detectChanges(currentData, newData);
      setChanges(detected);
    }
  }, [isOpen, currentData, newData]);

  const detectChanges = (current: RowData[], incoming: RowData[]): ChangeDetectionResult => {
    const result: ChangeDetectionResult = {
      added: [],
      updated: [],
      deleted: [],
      unchanged: [],
    };

    const currentMap = new Map(current.map(row => [row.account_number, row]));
    const newMap = new Map(incoming.map(row => [row.account_number, row]));

    // Find added and updated rows
    incoming.forEach(newRow => {
      const currentRow = currentMap.get(newRow.account_number);
      if (!currentRow) {
        result.added.push(newRow);
      } else {
        const changes = getFieldDifferences(currentRow, newRow);
        if (changes.length > 0) {
          result.updated.push({
            accountNumber: newRow.account_number,
            changes,
            oldRow: currentRow,
            newRow,
          });
        } else {
          result.unchanged.push(newRow);
        }
      }
    });

    // Find deleted rows
    current.forEach(currentRow => {
      if (!newMap.has(currentRow.account_number)) {
        result.deleted.push(currentRow);
      }
    });

    return result;
  };

  const getFieldDifferences = (oldRow: RowData, newRow: RowData): FieldChange[] => {
    const differences: FieldChange[] = [];
    const fieldsToCompare = ['property_name', 'unit_number', 'commodity_type', 'vendor_name', 'meter_number', 'gl_code', 'service_address'];
    
    fieldsToCompare.forEach(field => {
      const oldVal = oldRow[field] ?? '';
      const newVal = newRow[field] ?? '';
      if (String(oldVal).trim() !== String(newVal).trim()) {
        differences.push({
          field,
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    });

    return differences;
  };

  const handleConfirm = async () => {
    if (!changes) return;
    setIsLoading(true);
    try {
      await onConfirm(changes);
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const downloadComparisonReport = () => {
    if (!changes) return;
    
    const lines = [
      '# Re-upload Comparison Report',
      `Generated: ${new Date().toISOString()}`,
      `New File: ${newFileName}`,
      '',
      '## Summary',
      `- Added: ${changes.added.length} rows`,
      `- Updated: ${changes.updated.length} rows`,
      `- Deleted: ${changes.deleted.length} rows`,
      `- Unchanged: ${changes.unchanged.length} rows`,
      '',
      '## Updated Rows',
      ...changes.updated.flatMap(u => [
        `### Account #${u.accountNumber}`,
        ...u.changes.map(c => `- ${c.field}: "${c.oldValue}" → "${c.newValue}"`),
        '',
      ]),
      '## Added Rows',
      ...changes.added.map(a => `- Account #${a.account_number}`),
      '',
      '## Deleted Rows',
      ...changes.deleted.map(d => `- Account #${d.account_number}`),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparison-report-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SummaryCard = ({
    icon,
    label,
    count,
    color,
  }: {
    icon: React.ReactNode;
    label: string;
    count: number;
    color: 'green' | 'blue' | 'red' | 'gray';
  }) => {
    const colors = {
      green: 'bg-green-50 border-green-200 text-green-700',
      blue: 'bg-blue-50 border-blue-200 text-blue-700',
      red: 'bg-red-50 border-red-200 text-red-700',
      gray: 'bg-gray-50 border-gray-200 text-gray-700',
    };
    return (
      <Card className={cn('border', colors[color])}>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            {icon}
            <span className="text-2xl font-bold">{count}</span>
          </div>
          <p className="text-xs">{label}</p>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Re-upload Preview - Changes Detected
          </DialogTitle>
          <DialogDescription>
            Comparing: <strong>{newFileName}</strong> vs current data
          </DialogDescription>
        </DialogHeader>

        {changes && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <SummaryCard
                icon={<Plus className="h-4 w-4" />}
                label="to be ADDED"
                count={changes.added.length}
                color="green"
              />
              <SummaryCard
                icon={<RefreshCw className="h-4 w-4" />}
                label="to be UPDATED"
                count={changes.updated.length}
                color="blue"
              />
              <SummaryCard
                icon={<Trash2 className="h-4 w-4" />}
                label="to be DELETED"
                count={changes.deleted.length}
                color="red"
              />
              <SummaryCard
                icon={<Check className="h-4 w-4" />}
                label="UNCHANGED"
                count={changes.unchanged.length}
                color="gray"
              />
            </div>

            {/* Detailed Changes Tabs */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="added" className="gap-1">
                  <Plus className="h-3 w-3" />
                  New ({changes.added.length})
                </TabsTrigger>
                <TabsTrigger value="updated" className="gap-1">
                  <RefreshCw className="h-3 w-3" />
                  Updated ({changes.updated.length})
                </TabsTrigger>
                <TabsTrigger value="deleted" className="gap-1">
                  <Trash2 className="h-3 w-3" />
                  Deleted ({changes.deleted.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="updated" className="mt-4">
                {changes.updated.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No updated rows</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-32">Account #</TableHead>
                          <TableHead className="w-24">Status</TableHead>
                          <TableHead>Field</TableHead>
                          <TableHead>Change</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changes.updated.slice(0, 20).map((update, idx) => (
                          update.changes.map((change, cIdx) => (
                            <TableRow key={`${idx}-${cIdx}`}>
                              {cIdx === 0 && (
                                <>
                                  <TableCell rowSpan={update.changes.length} className="font-medium">
                                    {update.accountNumber}
                                  </TableCell>
                                  <TableCell rowSpan={update.changes.length}>
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                      UPDATE
                                    </Badge>
                                  </TableCell>
                                </>
                              )}
                              <TableCell className="capitalize">{change.field.replace(/_/g, ' ')}</TableCell>
                              <TableCell>
                                <span className="text-muted-foreground line-through mr-2">{change.oldValue || '(empty)'}</span>
                                <span className="text-primary">→</span>
                                <span className="font-medium ml-2">{change.newValue || '(empty)'}</span>
                              </TableCell>
                            </TableRow>
                          ))
                        ))}
                      </TableBody>
                    </Table>
                    {changes.updated.length > 20 && (
                      <p className="text-center text-sm text-muted-foreground py-2 border-t">
                        + {changes.updated.length - 20} more updated rows
                      </p>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="added" className="mt-4">
                {changes.added.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No new rows to add</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Account #</TableHead>
                          <TableHead>Property</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead>Commodity</TableHead>
                          <TableHead>Vendor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changes.added.slice(0, 20).map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{row.account_number}</TableCell>
                            <TableCell>{row.property_name || '-'}</TableCell>
                            <TableCell>{row.unit_number || '-'}</TableCell>
                            <TableCell>{row.commodity_type || '-'}</TableCell>
                            <TableCell>{row.vendor_name || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {changes.added.length > 20 && (
                      <p className="text-center text-sm text-muted-foreground py-2 border-t">
                        + {changes.added.length - 20} more new rows
                      </p>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="deleted" className="mt-4">
                {changes.deleted.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No rows will be deleted</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Account #</TableHead>
                          <TableHead>Property</TableHead>
                          <TableHead>Unit</TableHead>
                          <TableHead>Commodity</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {changes.deleted.map((row, idx) => (
                          <TableRow key={idx} className="bg-red-50/50">
                            <TableCell className="font-medium">{row.account_number}</TableCell>
                            <TableCell>{row.property_name || '-'}</TableCell>
                            <TableCell>{row.unit_number || '-'}</TableCell>
                            <TableCell>{row.commodity_type || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Warning for deletions */}
            {changes.deleted.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  ⚠️ {changes.deleted.length} rows will be deleted. This cannot be undone.
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={downloadComparisonReport} className="gap-2 mr-auto">
            <Download className="h-4 w-4" />
            Download Report
          </Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Applying...' : 'Confirm Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
